import { useEffect, useState } from "react";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import shareIcon from "@assets/icon-share.svg";
import {
  faFacebookF,
  faLinkedinIn,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAlacrity from "@hooks/useAlacrity";
import cx from "classnames";

const buttonClass =
  "shrink-0 rounded-full !bg-gray-250 w-10 h-10 hover:opacity-70";

type ArticleSocialSharing = {
  articleTitle: string;
};

export default function ArticleSocialSharing({
  articleTitle,
}: ArticleSocialSharing): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [shareUrl, setShareUrl] = useState<string>("");

  const { generateShortnerURL } = useAlacrity();

  const handleHover = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleShareUrl = async () => {
      if (typeof window === "undefined") return;
      const longURL = window?.location?.href;
      const shortURL = (await generateShortnerURL(longURL))?.data?.shortURL;
      setShareUrl(shortURL || longURL);
    };
    handleShareUrl();
  }, [generateShortnerURL]);

  return (
    <div
      className="relative cursor-pointer"
      onMouseEnter={handleHover}
      onMouseLeave={handleHover}
      data-testid="article-social-sharing-component"
    >
      <img
        src={shareIcon}
        width={40}
        height={40}
        className="hover:opacity-70"
        alt="Share this article."
      />
      {isOpen ? (
        <div
          className={cx(
            "absolute right-0 top-full z-50 flex space-x-2 border-t border-gray-250 bg-white px-4 py-3 shadow-lg",
            "lg:right-[unset] lg:-translate-x-1/2"
          )}
        >
          <FacebookShareButton url={shareUrl} className={buttonClass}>
            <FontAwesomeIcon icon={faFacebookF} />
          </FacebookShareButton>

          <TwitterShareButton
            url={shareUrl}
            title={articleTitle}
            className={buttonClass}
          >
            <FontAwesomeIcon icon={faXTwitter} />
          </TwitterShareButton>

          <WhatsappShareButton
            url={shareUrl}
            title={articleTitle}
            className={buttonClass}
          >
            <FontAwesomeIcon icon={faWhatsapp} size="lg" />
          </WhatsappShareButton>

          <LinkedinShareButton
            url={shareUrl}
            title={articleTitle}
            className={buttonClass}
          >
            <FontAwesomeIcon icon={faLinkedinIn} />
          </LinkedinShareButton>

          <TelegramShareButton
            url={shareUrl}
            title={articleTitle}
            className={buttonClass}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </TelegramShareButton>

          <EmailShareButton
            url={shareUrl}
            subject="Sharing from The Business Times"
            body={articleTitle}
            separator=" - "
            className={buttonClass}
          >
            <FontAwesomeIcon icon={faEnvelope} />
          </EmailShareButton>
        </div>
      ) : null}
    </div>
  );
}
